import { ANALYTICS_EVENTS, COMPETITION_PRIZE_TYPES } from '@nandosaus/constants';
import { get } from 'lodash';
import { useCallback, useState } from 'react';
import { useRootStore } from '@nandosaus/state-management';
import gql from 'graphql-tag';

import { analytics } from '../../../../analytics';
import { getApiClient } from '../../../../../lib/api-client';
import { STATUS } from '../../constants';

const COMPETITION_SPIN_MUTATION = gql`
  mutation enterCompetition($input: EnterCompetitionInput) {
    enterCompetition(input: $input) {
      success
      message
      prize {
        prizeType
        prizeName
        handle
        offer {
          barcode
          description
          discountAmount
          discountLimit
          discountType
          discountUsage
          expiryDate
          hash
          id
          image
          longDescription
          minOrderValue
          minimumOrderSpend
          name
          plus
          restaurantIds
          startDate
          type
          isPromoCode
        }
        giftCard {
          amount
          cardNumber
          verificationCode
        }
      }
      entriesRemaining
      hasConsentToEmailGuest
    }
  }
`;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const useSpin = ({ controls }) => {
  const [status, setStatus] = useState(STATUS.INITIAL);
  const [prize, setPrize] = useState();
  const [hasConsentToEmailGuest, setHasConsentToEmailGuest] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasMemberRecentlyEntered, setHasMemberRecentlyEntered] = useState(false);
  const { OfferStore, CompetitionStore } = useRootStore();

  const doSpin = useCallback(async () => {
    setStatus(STATUS.INITIAL);
    setErrorMessage(null);

    try {
      setStatus(STATUS.SPIN);
      await controls.start(STATUS.START);
      controls.start(STATUS.SPIN);

      // Let the spinner spin for a bit
      await sleep(3000);

      const client = await getApiClient();

      const input = {
        hasConsentToEmailGuest,
      };
      const response = await client.mutate({ mutation: COMPETITION_SPIN_MUTATION, variables: { input } });

      if (!response.data?.enterCompetition) {
        throw Error('An error occurred when trying to claim your prize. Please try again.');
      }

      const { enterCompetition } = response.data;

      setHasMemberRecentlyEntered(true);
      CompetitionStore.competition.setEntries(enterCompetition.entriesRemaining);

      if (!enterCompetition.success) {
        analytics.track(ANALYTICS_EVENTS.COMPETITION_ENTERED, {
          outcome: 'Lose',
        });

        await controls.start(STATUS.LOSE);
        await sleep(300);
        setStatus(STATUS.LOSE);
        return;
      }

      analytics.track(ANALYTICS_EVENTS.COMPETITION_ENTERED, {
        outcome: 'Win',
        prize: enterCompetition.prize?.prizeName,
      });

      if (enterCompetition.prize.prizeType === COMPETITION_PRIZE_TYPES.OFFER) {
        OfferStore.addOffer(enterCompetition.prize.offer);
      }

      setPrize(enterCompetition.prize);
      await controls.start(enterCompetition.prize.handle);
      await sleep(300);
      setStatus(STATUS.WIN);
    } catch (error) {
      const message = get(error, 'errors[0].message', error.message);
      await controls.start(STATUS.LOSE);
      await sleep(300);
      setErrorMessage(message);
      setStatus(STATUS.ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls, hasConsentToEmailGuest]);

  return {
    doSpin,
    errorMessage,
    status,
    setStatus,
    prize,
    hasMemberRecentlyEntered,
    hasConsentToEmailGuest,
    setHasConsentToEmailGuest,
  };
};

export { useSpin };
